import { CHART_PALETTE } from '@trajectory/ui';
import { Loader2, Play, Sparkles } from 'lucide-react';
import { useMemo } from 'react';
import type { ChannelSpec } from '@/charts/TelemetryChart';
import { TelemetryChart } from '@/charts/TelemetryChart';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { useCatalogLaunchSites, useCatalogReferenceRockets } from '@/hooks/useCatalog';
import { useResolvedTheme } from '@/hooks/useTheme';
import { useSimWorker, type BrowserSimulationOptions } from '@/hooks/useSimWorker';
import { buildMissionSimulationOptions } from '@/lib/ascent-program';
import { buildSimulationPreset, SIMULATION_PRESETS } from '@/lib/simulation-presets';
import { toPhysicsVehicle } from '@/lib/vehicle-adapter';
import { useMissionStore } from '@/stores/missionStore';
import { useRocketStore } from '@/stores/rocketStore';
import { useTelemetryStore } from '@/stores/telemetryStore';
import { useUiStore } from '@/stores/uiStore';

function chartGrid(
  colors: readonly string[],
): { title: string; description: string; channels: ChannelSpec[] }[] {
  return [
    {
      title: 'Altitude',
      description: 'Vehicle height above the reference ellipsoid throughout the simulated flight.',
      channels: [{ key: 'altitude', label: 'Altitude (m)', color: colors[0]! }],
    },
    {
      title: 'Speed',
      description: 'Inertial velocity compared with velocity relative to the rotating atmosphere.',
      channels: [
        { key: 'speed', label: 'Inertial (m/s)', color: colors[0]! },
        { key: 'relativeSpeed', label: 'Rel. to air (m/s)', color: colors[1]! },
      ],
    },
    {
      title: 'Dynamic pressure',
      description: 'Aerodynamic loading q = ½ρv²; its maximum is commonly called Max Q.',
      channels: [{ key: 'dynamicPressure', label: 'Q (Pa)', color: colors[2]! }],
    },
    {
      title: 'G-load',
      description:
        'Acceleration sensed by the vehicle and occupants, expressed in standard gravities.',
      channels: [{ key: 'gLoad', label: 'Sensed accel. (g)', color: colors[3]! }],
    },
    {
      title: 'Vehicle mass',
      description: 'Total flying mass as propellant burns and stages or fairings are discarded.',
      channels: [{ key: 'mass', label: 'Mass (kg)', color: colors[4]! }],
    },
    {
      title: 'Throttle',
      description:
        'Commanded fraction of available engine thrust; 1 represents full rated throttle.',
      channels: [{ key: 'throttle', label: 'Throttle (0-1)', color: colors[5]! }],
    },
    {
      title: 'Mach number',
      description: 'Air-relative speed divided by the local atmospheric speed of sound.',
      channels: [{ key: 'mach', label: 'Mach', color: colors[6]! }],
    },
    {
      title: 'Apsides',
      description:
        'Predicted highest and lowest orbital altitudes; both must clear Earth for a bound orbit.',
      channels: [
        { key: 'apoapsisAltitude', label: 'Apoapsis alt. (m)', color: colors[0]! },
        { key: 'periapsisAltitude', label: 'Periapsis alt. (m)', color: colors[1]! },
      ],
    },
    {
      title: 'Attitude',
      description:
        'Flight-path, commanded pitch, and angle of attack reveal guidance and aerodynamic alignment.',
      channels: [
        { key: 'flightPathAngleDeg', label: 'Flight path (°)', color: colors[0]! },
        { key: 'pitchDeg', label: 'Pitch (°)', color: colors[1]! },
        { key: 'angleOfAttackDeg', label: 'AoA (°)', color: colors[2]! },
      ],
    },
    {
      title: 'Delta-V budget',
      description:
        'Propulsive velocity spent and the portions lost to gravity and atmospheric drag.',
      channels: [
        { key: 'deltaVExpended', label: 'Expended (m/s)', color: colors[0]! },
        { key: 'dragLoss', label: 'Drag loss (m/s)', color: colors[3]! },
        { key: 'gravityLoss', label: 'Gravity loss (m/s)', color: colors[5]! },
      ],
    },
  ];
}

function RunSimulationButton({ run }: { run: (options: BrowserSimulationOptions) => void }) {
  const missionDocument = useMissionStore((state) => state.document);
  const rocketDocument = useRocketStore((state) => state.document);
  const launchSites = useCatalogLaunchSites();
  const status = useTelemetryStore((state) => state.status);

  const handleRun = () => {
    if (!missionDocument || !rocketDocument) return;
    const launchSite = launchSites.data?.find((site) => site.id === missionDocument.launchSiteId);
    if (!launchSite && !missionDocument.launchSiteOverride) {
      useUiStore.getState().notify({
        kind: 'warning',
        title: 'Unknown launch site',
        message: 'Catalogue data has not loaded yet.',
      });
      return;
    }
    try {
      const options = buildMissionSimulationOptions({
        mission: missionDocument,
        vehicle: toPhysicsVehicle(rocketDocument),
        launchSite: launchSite!,
      });
      run(options);
    } catch (error) {
      useUiStore.getState().notify({
        kind: 'danger',
        title: 'Could not build a flight plan',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const disabledReason = !missionDocument
    ? 'Select a mission first'
    : !rocketDocument
      ? 'The mission has no rocket assigned'
      : undefined;

  return (
    <button
      type="button"
      onClick={handleRun}
      disabled={Boolean(disabledReason) || status === 'running'}
      title={
        disabledReason ??
        'Run the complete configured mission using its rocket, launch site, guidance, environment, and target'
      }
      className="flex items-center gap-2 rounded-control border border-border bg-surface-raised px-3 py-1.5 text-xs text-text hover:bg-surface-sunken disabled:opacity-40"
    >
      {status === 'running' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Play className="h-3.5 w-3.5" />
      )}
      {status === 'running'
        ? 'Simulating…'
        : status === 'ready'
          ? 'Re-run full mission'
          : 'Run full mission'}
    </button>
  );
}

function DemoPresetButtons({ run }: { run: (options: BrowserSimulationOptions) => void }) {
  const launchSites = useCatalogLaunchSites();
  const rockets = useCatalogReferenceRockets();
  const status = useTelemetryStore((state) => state.status);
  const launchSite =
    launchSites.data?.find((site) => site.id === 'ccsfs-slc40') ?? launchSites.data?.[0];
  const rocket =
    rockets.data?.find((entry) => entry.vehicle.id === 'falcon-9-block-5') ?? rockets.data?.[0];
  const catalogReady = Boolean(launchSite && rocket);

  return (
    <div className="flex items-center gap-1.5 border-l border-border pl-2">
      <span className="flex items-center gap-1 text-[11px] text-faint">
        <Sparkles className="h-3 w-3" /> Quick start
      </span>
      {SIMULATION_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          disabled={!catalogReady || status === 'running'}
          title={catalogReady ? preset.description : 'Loading the demo vehicle and launch site…'}
          onClick={() => {
            if (!launchSite || !rocket) return;
            run(buildSimulationPreset(preset.id, rocket.vehicle, launchSite));
            useUiStore.getState().notify({
              kind: 'info',
              title: `${preset.name} started`,
              message: 'This quick-start run does not change your open mission or rocket.',
            });
          }}
          className="rounded-control border border-border bg-surface px-2 py-1.5 text-xs text-muted hover:bg-surface-sunken hover:text-text disabled:opacity-40"
        >
          {preset.name}
        </button>
      ))}
    </div>
  );
}

export function ChartsPanel() {
  const status = useTelemetryStore((state) => state.status);
  const error = useTelemetryStore((state) => state.error);
  const resolvedTheme = useResolvedTheme();
  const { run } = useSimWorker();
  // Memoized so each `<TelemetryChart>` only recreates its uPlot instance
  // when the theme actually changes, not on every unrelated re-render here.
  const grid = useMemo(() => chartGrid(CHART_PALETTE[resolvedTheme].series), [resolvedTheme]);

  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <PanelLearningBanner panelId="charts" />
      <div className="flex items-center gap-2 px-1">
        <RunSimulationButton run={run} />
        <DemoPresetButtons run={run} />
        {status === 'error' && error && (
          <span className="truncate text-xs text-danger">{error}</span>
        )}
      </div>
      <div className="grid flex-1 grid-cols-2 gap-2 overflow-auto xl:grid-cols-3">
        {grid.map((chart) => (
          <div
            key={chart.title}
            title={chart.description}
            aria-label={`${chart.title}: ${chart.description}`}
            className="min-h-[180px] rounded-panel border border-border bg-surface-raised p-1"
          >
            <TelemetryChart title={chart.title} channels={chart.channels} />
          </div>
        ))}
      </div>
    </div>
  );
}
